import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Mail, UserCheck, Users, Globe, Video, Clock, CheckCircle2, Archive, ExternalLink } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import MeetingPanel from "./MeetingPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = [
  { v: "new", label: "New", icon: Clock, color: "bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)]" },
  { v: "contacted", label: "Contacted", icon: Mail, color: "bg-blue-100 text-blue-800" },
  { v: "replied", label: "Replied", icon: CheckCircle2, color: "bg-green-100 text-green-800" },
  { v: "closed", label: "Closed", icon: Archive, color: "bg-gray-200 text-gray-700" },
];

export default function LeadsTab({ token }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState("marketing");
  const [data, setData] = useState({ marketing: [], community: [], contact: [], team: [] });
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Password": token } });
      setData(data);
    } catch { toast({ title: "Could not load leads", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const setStatus = async (lead, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${lead.kind}/${lead.id}`, { status }, { headers: { "X-Admin-Password": token } });
      setData((d) => ({ ...d, [lead.kind]: d[lead.kind].map((l) => (l.id === lead.id ? { ...l, status } : l)) }));
      toast({ title: `Marked ${status}` });
    } catch { toast({ title: "Update failed", variant: "destructive" }); }
  };

  const list = data[bucket] || [];
  const newCount = ["marketing", "community", "contact", "team"].reduce((n, k) => n + (data[k] || []).filter((l) => l.status === "new").length, 0);

  return (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6" data-testid="admin-leads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Leads inbox</h2>
          <p className="text-sm text-[color:var(--dms-navy)]/60 mt-1">
            {newCount > 0 ? <span className="text-[color:var(--dms-peach)] font-semibold">{newCount} new</span> : "All caught up"} · {data.marketing?.length || 0} marketing · {data.community?.length || 0} community · {data.contact?.length || 0} contact · {data.team?.length || 0} grow your team
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]" data-testid="admin-leads-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        <BucketPill active={bucket === "marketing"} onClick={() => setBucket("marketing")} testid="admin-leads-pill-marketing">
          <Video className="w-4 h-4" /> Marketing &amp; Websites ({data.marketing?.length || 0})
        </BucketPill>
        <BucketPill active={bucket === "community"} onClick={() => setBucket("community")} testid="admin-leads-pill-community">
          <Users className="w-4 h-4" /> Community ({data.community?.length || 0})
        </BucketPill>
        <BucketPill active={bucket === "contact"} onClick={() => setBucket("contact")} testid="admin-leads-pill-contact">
          <Mail className="w-4 h-4" /> Contact messages ({data.contact?.length || 0})
        </BucketPill>
        <BucketPill active={bucket === "team"} onClick={() => setBucket("team")} testid="admin-leads-pill-team">
          <UserCheck className="w-4 h-4" /> Grow Your Team ({data.team?.length || 0})
        </BucketPill>
      </div>

      <div className="mt-6 space-y-3">
        {list.length === 0 && !loading && (
          <div className="text-center py-16 text-[color:var(--dms-navy)]/50 text-sm" data-testid="admin-leads-empty">No {bucket} leads yet.</div>
        )}
        {list.map((lead) => {
          const open = expanded === lead.id;
          return (
            <div key={lead.id} className="border border-[color:var(--dms-navy)]/10 rounded-xl overflow-hidden" data-testid={`admin-lead-${lead.id}`}>
              <button onClick={() => setExpanded(open ? null : lead.id)} data-testid={`admin-lead-toggle-${lead.id}`} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[color:var(--dms-cream)]/50 transition">
                <div className="flex items-center gap-4 min-w-0">
                  <StatusBadge status={lead.status} />
                  <div className="min-w-0">
                    <p className="font-serif text-base text-[color:var(--dms-navy)] truncate">{lead.name}</p>
                    <p className="text-xs text-[color:var(--dms-navy)]/60 truncate">{lead.email}{lead.business_name ? ` · ${lead.business_name}` : ""}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] text-[color:var(--dms-navy)]/50">{formatDate(lead.created_at)}</p>
                  {bucket === "marketing" && lead.fields?.service && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[color:var(--dms-peach-light)]/50 text-[10px] uppercase tracking-widest text-[color:var(--dms-navy)] font-semibold">{lead.fields.service}</span>
                  )}
                  {bucket === "team" && lead.fields?.meeting?.status === "scheduled" && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-green-100 text-[10px] uppercase tracking-widest text-green-800 font-semibold" data-testid={`admin-lead-meeting-badge-${lead.id}`}>Meeting set</span>
                  )}
                  {bucket === "community" && lead.fields?.group && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[color:var(--dms-peach-light)]/50 text-[10px] uppercase tracking-widest text-[color:var(--dms-navy)] font-semibold">{lead.fields.group}</span>
                  )}
                </div>
              </button>

              {open && (
                <div className="px-5 pb-5 pt-2 border-t border-[color:var(--dms-navy)]/10 bg-[color:var(--dms-cream)]/40">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    {Object.entries(lead.fields || {}).map(([k, v]) =>
                      v && k !== "meeting" ? (
                        <Row key={k} label={k.replace(/_/g, " ")} value={typeof v === "string" ? v : Array.isArray(v) ? v.join(", ") : JSON.stringify(v)} />
                      ) : null
                    )}
                  </div>

                  {lead.kind === "team" && (
                    <MeetingPanel lead={lead} token={token} onChange={(meeting) => setData((d) => ({ ...d, team: d.team.map((l) => (l.id === lead.id ? { ...l, status: l.status === "new" && meeting.status === "scheduled" ? "contacted" : l.status, fields: { ...l.fields, meeting } } : l)) }))} />
                  )}

                  <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <a href={`mailto:${lead.email}?subject=Re:%20Your%20Design%20My%20Story%20enquiry`} className="btn-peach" data-testid={`admin-lead-reply-${lead.id}`}>
                      <Mail className="w-4 h-4" /> Reply by email <ExternalLink className="w-3 h-3" />
                    </a>
                    <span className="text-xs text-[color:var(--dms-navy)]/50 mx-1">Set status:</span>
                    {STATUS_FLOW.map((s) => (
                      <button
                        key={s.v}
                        onClick={() => setStatus(lead, s.v)}
                        disabled={lead.status === s.v}
                        data-testid={`admin-lead-status-${lead.id}-${s.v}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase font-semibold transition ${lead.status === s.v ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]/70 hover:border-[color:var(--dms-navy)]"}`}
                      >
                        <s.icon className="w-3 h-3" /> {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BucketPill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${active ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]"}`}>
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_FLOW.find((x) => x.v === status) || STATUS_FLOW[0];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-widest uppercase font-semibold flex-shrink-0 ${s.color}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest uppercase text-[color:var(--dms-navy)]/50 font-semibold">{label}</p>
      <p className="text-sm text-[color:var(--dms-navy)] mt-0.5 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
